
import React, { useEffect, useState } from "react";
import api from "../api";
import { toast } from "react-toastify";

const AddCategory = () => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    status: true,
  });

  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState({});

  // Release the previous preview URL when it changes or the component unmounts.
  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: undefined,
    }));

    setMessage("");
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0] || null;

    if (file && !file.type.startsWith("image/")) {
      toast.error("Please select a valid image file.");
      e.target.value = "";
      return;
    }

    setImage(file);
    setPreview(file ? URL.createObjectURL(file) : null);

    setErrors((prev) => ({
      ...prev,
      image: undefined,
    }));
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      status: true,
    });

    setImage(null);
    setPreview(null);
    setErrors({});
    setMessage("");

    const fileInput = document.getElementById("category-image");
    if (fileInput) fileInput.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (loading) return;

    setLoading(true);
    setMessage("");
    setErrors({});

    const form = new FormData();
    form.append("name", formData.name.trim());
    form.append("description", formData.description);
    form.append("status", formData.status ? "1" : "0");

    if (image) {
      form.append("image", image);
    }

    try {
      const token = localStorage.getItem("token");

      const res = await api.post("add-category", form, {
        headers: {
          Accept: "application/json",
          ...(token
            ? { Authorization: `Bearer ${token}` }
            : {}),
        },
      });

      if (res.data?.status === false) {
        throw new Error(
          res.data.message || "Failed to add category."
        );
      }

      const successMessage =
        res.data?.message || "Category added successfully!";

      toast.success(successMessage);
      setMessage(successMessage);
      resetForm();
    } catch (err) {
      console.error("Add category error:", err);

      const responseData = err.response?.data;

      if (err.response?.status === 422) {
        const validationErrors = responseData?.errors || {};
        setErrors(validationErrors);

        const firstError = Object.values(validationErrors)
          .flat()
          .find(Boolean);

        const errorMessage =
          firstError ||
          responseData?.message ||
          "Please fix the errors below.";

        setMessage(errorMessage);
        toast.error(errorMessage);
      } else {
        const errorMessage =
          responseData?.message ||
          (err.response
            ? "Unable to add category. Please try again."
            : "Network error. Check your server connection.");

        setMessage(errorMessage);
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const inputClass = (field) =>
    `w-full rounded-xl border bg-[#111111] px-4 py-3 text-white placeholder-gray-500 outline-none transition focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/20 ${
      errors[field]
        ? "border-red-500"
        : "border-[#3B3320]"
    }`;

  const labelClass =
    "mb-2 block text-sm font-medium text-gray-200";

  const errorText = (field) =>
    errors[field] ? (
      <p className="mt-1.5 text-sm text-red-400">
        {Array.isArray(errors[field])
          ? errors[field][0]
          : errors[field]}
      </p>
    ) : null;

  return (
    <div className="min-h-screen bg-[#080808] px-4 py-8 text-white sm:px-6 lg:py-12">
      <div className="mx-auto max-w-2xl">
        {/* Header */}
        <div className="relative mb-8 overflow-hidden rounded-2xl border border-[#6B541A] bg-gradient-to-br from-[#211B0B] via-[#14120C] to-black px-6 py-8 shadow-xl sm:px-8">
          <div className="absolute right-0 top-0 h-32 w-32 rounded-full bg-[#D4AF37]/10 blur-3xl" />

          <div className="relative flex items-center gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-[#D4AF37]/50 bg-[#D4AF37]/10 text-3xl text-[#D4AF37]">
              +
            </div>

            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-[0.25em] text-[#D4AF37]">
                Category Management
              </p>
              <h1 className="text-2xl font-bold sm:text-3xl">
                Add New Category
              </h1>
              <p className="mt-2 text-sm text-gray-400">
                Create and manage your product categories.
              </p>
            </div>
          </div>
        </div>

        {/* Form Card */}
        <div className="rounded-2xl border border-[#3B3320] bg-[#101010] p-5 shadow-2xl sm:p-8">
          {message && (
            <div
              role="status"
              className={`mb-6 rounded-xl border p-4 text-sm ${
                errors && Object.keys(errors).length > 0
                  ? "border-red-500/30 bg-red-500/10 text-red-300"
                  : "border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#F1D675]"
              }`}
            >
              {message}
            </div>
          )}

          <form
            onSubmit={handleSubmit}
            className="space-y-6"
          >
            {/* Category Name */}
            <div>
              <label htmlFor="category-name" className={labelClass}>
                Category Name <span className="text-[#D4AF37]">*</span>
              </label>

              <input
                id="category-name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter category name"
                className={inputClass("name")}
                required
                maxLength={255}
              />

              {errorText("name")}
            </div>

            {/* Description */}
            <div>
              <label
                htmlFor="category-description"
                className={labelClass}
              >
                Description
                <span className="ml-2 text-xs font-normal text-gray-500">
                  (Optional)
                </span>
              </label>

              <textarea
                id="category-description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter a description for this category"
                rows={4}
                className={`${inputClass("description")} min-h-[120px] resize-y`}
              />

              {errorText("description")}
            </div>

            {/* Category Image */}
            <div>
              <label htmlFor="category-image" className={labelClass}>
                Category Image
                <span className="ml-2 text-xs font-normal text-gray-500">
                  (Optional)
                </span>
              </label>

              <div className="rounded-xl border border-dashed border-[#6B541A] bg-black/40 p-4 sm:p-5">
                <input
                  id="category-image"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="w-full text-sm text-gray-400 file:mr-4 file:cursor-pointer file:rounded-lg file:border-0 file:bg-[#D4AF37] file:px-4 file:py-2.5 file:font-semibold file:text-black file:transition hover:file:bg-[#E8C85A]"
                />

                <p className="mt-3 text-xs text-gray-500">
                  Select an image for your category.
                </p>

                {errorText("image")}

                {preview && (
                  <div className="mt-5">
                    <div className="mb-2 flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-300">
                        Image Preview
                      </p>

                      <button
                        type="button"
                        onClick={() => {
                          setImage(null);
                          setPreview(null);

                          const fileInput =
                            document.getElementById("category-image");

                          if (fileInput) fileInput.value = "";

                          setErrors((prev) => ({
                            ...prev,
                            image: undefined,
                          }));
                        }}
                        className="text-xs font-medium text-red-400 transition hover:text-red-300"
                      >
                        Remove image
                      </button>
                    </div>

                    <div className="inline-flex rounded-xl border border-[#6B541A] bg-[#17140C] p-2">
                      <img
                        src={preview}
                        alt="Category preview"
                        className="h-40 w-40 rounded-lg object-cover"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Status */}
            <div className="flex items-center justify-between gap-4 rounded-xl border border-[#3B3320] bg-[#17140C] p-4">
              <div>
                <p className="font-medium text-white">
                  Category Status
                </p>
                <p className="mt-1 text-sm text-gray-400">
                  {formData.status
                    ? "Active and visible to customers"
                    : "Inactive"}
                </p>
              </div>

              <label className="relative inline-flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  name="status"
                  checked={formData.status}
                  onChange={handleChange}
                  className="peer sr-only"
                />

                <span className="h-7 w-12 rounded-full bg-gray-700 transition peer-checked:bg-[#D4AF37] peer-focus-visible:ring-2 peer-focus-visible:ring-[#D4AF37] peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-[#101010]" />

                <span className="absolute left-1 top-1 h-5 w-5 rounded-full bg-white shadow transition peer-checked:translate-x-5 peer-checked:bg-black" />

                <span className="sr-only">
                  Toggle category status
                </span>
              </label>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-3 rounded-xl bg-gradient-to-r from-[#B89427] via-[#E2C253] to-[#B89427] px-6 py-4 font-bold text-black shadow-lg shadow-[#D4AF37]/10 transition duration-200 hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:ring-offset-2 focus:ring-offset-[#101010] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? (
                <>
                  <span className="h-5 w-5 animate-spin rounded-full border-2 border-black/30 border-t-black" />
                  Adding Category...
                </>
              ) : (
                <>
                  <span className="text-xl">+</span>
                  Add Category
                </>
              )}
            </button>

            <p className="text-center text-xs text-gray-500">
              Fields marked with{" "}
              <span className="text-[#D4AF37]">*</span> are required.
            </p>
          </form>
        </div>

        <p className="mt-6 text-center text-xs text-gray-600">
          Product Management System
        </p>
      </div>
    </div>
  );
};

export default AddCategory;